/**
 * HiddenCustomFieldsHint — "why isn't my field here?", answered where it is asked.
 *
 * A custom field scoped to a relationship the record does not carry renders
 * nothing and explains nothing, and neither does one defined on the OTHER
 * relationship-bearing record type (a relationship is tenant-global; a
 * definition belongs to exactly one `entity_type`). Both are correct behaviour
 * and both look identical from the record: the admin adds a field, opens a
 * record that "has that relationship", and sees the older field alone.
 *
 * This is the sentence that was missing. It names every active definition for
 * this record type that the record does NOT inherit, with the relationships each
 * waits for, and — for the other record type — how many fields those same
 * relationships bear there instead.
 *
 * Collapsed by default and muted: it is a diagnosis, not a control, so it must
 * never compete with the fields the record actually has — but it renders for
 * the cross-record-type count ALONE, because that case (a record whose own type
 * defines nothing) otherwise produces no custom-fields card and therefore no
 * symptom of any kind. See docs/platform/custom-fields-design.md.
 */
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';

import { Button } from '../../../components/ui/button';
import { CollapsibleSection } from '../common/CollapsibleSection';
import { ltrIsolated } from '../common/LtrToken';
import { useAuth } from '../../auth';
import {
  RELATIONSHIP_SCOPED_ENTITIES,
  resolveCustomFieldLabel,
  resolveScopeTokenLabel,
} from './customFieldUtils';
import { useOtherRecordTypeFieldCount } from './useOtherRecordTypeFieldCount';
import {
  CUSTOM_FIELDS_ADMIN_PATH,
  useRelationshipClasses,
} from './useRelationshipClasses';
import type { CustomFieldDefinition } from '../../../generated/api/models/customFieldDefinition';

export interface HiddenCustomFieldsHintProps {
  /** Entity registry key of the record being viewed (e.g. 'contact'). */
  entityType: string;
  /** The record's effective relationship scope tokens (built-in + `class:<id>`). */
  relationships: string[];
  /** Active definitions for this record type that the record does NOT inherit. */
  hidden: CustomFieldDefinition[];
}

export function HiddenCustomFieldsHint({
  entityType,
  relationships,
  hidden,
}: HiddenCustomFieldsHintProps) {
  const { t } = useTranslation(['custom_fields', 'common']);
  // One registry read: the label group for this entity's built-in relationships
  // AND the i18n key naming its sibling record type. That key lives in the
  // registry rather than beside the sentence because the record-type noun is a
  // tenant-customizable term and has to sit INSIDE the translated string.
  const scoped = RELATIONSHIP_SCOPED_ENTITIES[entityType];

  // Archived classes included: a definition still scoped to one is a real
  // answer ("applies to <archived class>"), not an unknown token.
  const { data: relationshipClasses } = useRelationshipClasses({ includeInactive: true });
  const classesById = useMemo(
    () => new Map((relationshipClasses ?? []).map((c) => [c.id, c])),
    [relationshipClasses],
  );
  const otherCount = useOtherRecordTypeFieldCount(entityType, relationships);

  if (!scoped || (hidden.length === 0 && otherCount === 0)) return null;

  // The trigger counts what is missing HERE; when nothing is, the record type is
  // the whole answer and the label says so instead of "0 fields".
  const label =
    hidden.length > 0
      ? t('custom_fields:hiddenFields.label', { count: hidden.length })
      : t('custom_fields:hiddenFields.labelElsewhere');

  return (
    <CollapsibleSection label={label}>
      <div className="space-y-2">
        {hidden.length > 0 && (
          <>
            <p className="text-sm text-muted-foreground">{t('custom_fields:hiddenFields.help')}</p>
            <ul className="space-y-1">
              {hidden.map((definition) => (
                <li key={definition.id} className="text-sm">
                  <span className="text-foreground">{resolveCustomFieldLabel(definition)}</span>
                  <span className="text-muted-foreground">
                    {' — '}
                    {t('custom_fields:hiddenFields.appliesTo', {
                      // Each label is bidi-ISOLATED before joining: a tenant
                      // class label is content of unknown language (Hebrew here,
                      // routinely), and interpolating it bare into a translated
                      // sentence lets the paragraph's direction reorder it
                      // against the commas. There is no element to wrap because
                      // the value goes THROUGH `t()`, so the isolate is the
                      // character-level form of `<BidiText>`.
                      relationships: (definition.applies_to_relationships ?? [])
                        .map((token) =>
                          ltrIsolated(
                            resolveScopeTokenLabel(token, scoped.labelGroup, classesById, t)
                          )
                        )
                        .join(', '),
                    })}
                  </span>
                </li>
              ))}
            </ul>
          </>
        )}
        {otherCount > 0 && (
          <p className="text-sm text-muted-foreground">
            {t(scoped.otherCountKey, { count: otherCount })}
          </p>
        )}
        <ManageFieldsLink />
      </div>
    </CollapsibleSection>
  );
}

/** Link to the admin page — only for the admins the route actually admits. */
function ManageFieldsLink() {
  const { t } = useTranslation('custom_fields');
  const { user } = useAuth();
  if (!user?.is_admin) return null;
  return (
    <Button variant="outline" size="sm" asChild>
      <Link to={CUSTOM_FIELDS_ADMIN_PATH}>{t('custom_fields:hiddenFields.manage')}</Link>
    </Button>
  );
}
